import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import VehicleCarousel from '../../components/VehicleCarousel';
import { 
  ChevronRight, ChevronDown, Check, Zap, Shield, Clock, 
  Users, FileUp, Settings2, Wrench, Download, Phone, Mail,
  MapPin, Menu, X, Star, ArrowRight, Gauge, Upload, Cpu,
  CreditCard, Globe, Gift
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const LandingPage = () => {
  const { t, locale, setLocale, availableLocales } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: Clock, title: t('landing.features.f1'), desc: t('landing.features.f1d') },
    { icon: Shield, title: t('landing.features.f2'), desc: t('landing.features.f2d') },
    { icon: Users, title: t('landing.features.f3'), desc: t('landing.features.f3d') },
    { icon: Star, title: t('landing.features.f4'), desc: t('landing.features.f4d') },
    { icon: Cpu, title: t('landing.features.f5'), desc: t('landing.features.f5d') },
    { icon: Globe, title: t('landing.features.f6'), desc: t('landing.features.f6d') },
  ];

  const tuningServices = [
    { name: 'Stage 1', desc: 'Kennfeldoptimierung' },
    { name: 'Stage 2', desc: 'Performance Tuning' },
    { name: 'DPF OFF', desc: 'Partikelfilter' },
    { name: 'EGR OFF', desc: 'Abgasrückführung' },
    { name: 'AdBlue OFF', desc: 'AdBlue System' },
    { name: 'Pop & Bang', desc: 'Schubabschaltung' },
    { name: 'Launch Control', desc: 'Startautomatik' },
    { name: 'VMAX Aufhebung', desc: 'Geschwindigkeitsbegrenzer' },
    { name: 'E85 Flexfuel', desc: 'Ethanol-Anpassung' },
  ];

  const steps = [
    { num: '01', title: t('landing.steps.s1'), desc: t('landing.steps.s1d'), icon: Users },
    { num: '02', title: t('landing.steps.s2'), desc: t('landing.steps.s2d'), icon: CreditCard },
    { num: '03', title: t('landing.steps.s3'), desc: t('landing.steps.s3d'), icon: Upload },
    { num: '04', title: t('landing.steps.s4'), desc: t('landing.steps.s4d'), icon: Settings2 },
    { num: '05', title: t('landing.steps.s5'), desc: t('landing.steps.s5d'), icon: Download },
  ];

  const faqs = [
    { q: t('landing.faq.q1'), a: t('landing.faq.a1') },
    { q: t('landing.faq.q2'), a: t('landing.faq.a2') },
    { q: t('landing.faq.q3'), a: t('landing.faq.a3') },
    { q: t('landing.faq.q4'), a: t('landing.faq.a4') },
    { q: t('landing.faq.q5'), a: t('landing.faq.a5') },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="landing-page">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0D0D0D]/95 backdrop-blur-xl border-b border-white/5' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
              <div className="w-10 h-10 bg-[#EC2229] rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(236,34,41,0.3)]">
                <span className="font-heading font-bold text-white text-lg">TX</span>
              </div>
              <div>
                <span className="font-heading font-bold text-white text-xl">TrXL</span>
                <span className="text-[#EC2229] font-heading font-bold ml-1">Performance</span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {[
                { href: '#leistungen', label: t('landing.nav.services') },
                { href: '#services', label: t('landing.nav.tuning') },
                { href: '#ablauf', label: t('landing.nav.process') },
                { href: '#faq', label: t('landing.nav.faq') },
                { href: '#kontakt', label: t('landing.nav.contact') },
              ].map(link => (
                <a key={link.href} href={link.href} className="text-sm text-white/50 hover:text-white transition-colors tracking-wide uppercase font-medium">
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 px-3 py-2 text-white/40 hover:text-white transition-colors" data-testid="language-selector">
                    <span className="text-lg">{availableLocales.find(l => l.code === locale)?.flag}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1A1D23] border-white/10">
                  {availableLocales.map(l => (
                    <DropdownMenuItem key={l.code} onClick={() => setLocale(l.code)} className={locale === l.code ? 'bg-white/5' : ''}>
                      <span className="mr-2">{l.flag}</span>
                      <span>{l.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/5 font-medium" data-testid="login-btn">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#EC2229] hover:bg-[#D01F25] text-white font-semibold px-6 shadow-[0_0_20px_rgba(236,34,41,0.25)]" data-testid="register-btn">
                  Jetzt starten
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>

              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-white p-2">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#0D0D0D] border-t border-white/5 px-4 py-6">
            <nav className="flex flex-col gap-4">
              {['Leistungen', 'Services', 'Ablauf', 'FAQ'].map(label => (
                <a key={label} href={`#${label.toLowerCase()}`} className="text-white/60 hover:text-white" onClick={() => setMobileMenuOpen(false)}>
                  {label}
                </a>
              ))}
              <Link to="/login" className="text-white/60 hover:text-white" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img src="/images/hero/engine-bay.png" alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/90 to-[#0D0D0D]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-[#0D0D0D]/50" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EC2229]/8 rounded-full border border-[#EC2229]/15 mb-8">
                <div className="w-2 h-2 rounded-full bg-[#EC2229] animate-pulse" />
                <span className="text-xs text-[#EC2229] font-semibold tracking-widest uppercase">Professional ECU File Service</span>
              </div>
              
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6">
                {t('landing.hero.title1')}<br />
                <span className="text-[#EC2229]">{t('landing.hero.title2')}</span> {t('landing.hero.title3')}
              </h1>
              
              <p className="text-lg text-white/40 max-w-xl mb-6 leading-relaxed">
                {t('landing.hero.subtitle')}
              </p>

              {/* Promo Badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-semibold">1 File GRATIS bei Neuanmeldung</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full border border-amber-500/20">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-amber-400 font-semibold">Ab 30 EUR pro File</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-16">
                <Link to="/register">
                  <Button className="bg-[#EC2229] hover:bg-[#D01F25] text-white text-lg px-8 py-6 font-semibold shadow-[0_0_30px_rgba(236,34,41,0.3)] transition-all hover:shadow-[0_0_40px_rgba(236,34,41,0.4)]" data-testid="hero-cta">
                    {t('landing.hero.cta')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#ablauf">
                  <Button variant="outline" className="border-white/10 text-white/70 hover:text-white hover:bg-white/5 text-lg px-8 py-6">
                    {t('landing.hero.ctaSecondary')}
                  </Button>
                </a>
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap gap-12">
                {[
                  { value: t('landing.hero.stat1v'), label: t('landing.hero.stat1l') },
                  { value: t('landing.hero.stat2v'), label: t('landing.hero.stat2l') },
                  { value: t('landing.hero.stat3v'), label: t('landing.hero.stat3l') },
                  { value: t('landing.hero.stat4v'), label: t('landing.hero.stat4l') },
                ].map((stat, i) => (
                  <div key={i} className="text-left">
                    <p className="text-2xl font-heading font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-white/30 tracking-wider uppercase mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="hidden lg:block relative">
              <div className="relative rounded-2xl overflow-hidden border border-white/5 shadow-2xl shadow-black/50">
                <img src="/images/hero/ecu-tuning.png" alt="ECU Chiptuning" className="w-full h-auto" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/60 via-transparent to-transparent" />
              </div>
              {/* Badge removed per user request */}
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Carousel */}
      <VehicleCarousel />

      {/* Features Section */}
      <section id="leistungen" className="py-24 bg-[#0D0D0D] relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-[#EC2229] tracking-[0.15em] uppercase mb-3">{t('landing.features.title')}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-white/40 max-w-2xl mx-auto">
              {t('landing.hero.subtitle').substring(0, 80)}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, i) => (
              <div key={i} className="group p-6 rounded-xl bg-[#141418] border border-white/[0.04] hover:border-[#EC2229]/20 transition-all duration-300">
                <div className="w-12 h-12 rounded-lg bg-[#EC2229]/10 flex items-center justify-center mb-4 group-hover:bg-[#EC2229]/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#EC2229]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#0A0A0A] relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EC2229]/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-[#EC2229] tracking-[0.15em] uppercase mb-3">{t('landing.services.title')}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Tuning-<span className="text-[#EC2229]">Optionen</span>
            </h2>
            <p className="text-white/40 max-w-2xl mx-auto">
              {t('landing.services.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tuningServices.map((service, i) => (
              <div key={i} className="flex items-center justify-between p-5 rounded-xl bg-[#141418] border border-white/[0.04] hover:border-[#EC2229]/20 transition-all group">
                <div>
                  <h3 className="font-heading font-semibold text-white group-hover:text-[#EC2229] transition-colors">{service.name}</h3>
                  <p className="text-xs text-white/30 mt-0.5">{service.desc}</p>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <Check className="w-5 h-5 text-emerald-500/60" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-[#141418] border border-white/[0.04] mb-4">
              <span className="text-white/50 text-sm">Fileservice</span>
              <span className="font-heading text-2xl font-bold text-[#EC2229]">{t('landing.pricing.from')} 30€</span>
              <span className="text-white/30 text-xs">{t('landing.pricing.perFile')}</span>
            </div>
            <p className="text-sm text-white/30 mb-4">Über 35 Tuning-Optionen individuell wählbar - attraktive Rabatte bei großen Credit-Paketen</p>
            <Link to="/register">
              <Button className="bg-[#EC2229] hover:bg-[#D01F25] text-white font-semibold px-8">
                Alle Optionen entdecken
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Credit Packages Promo */}
      <section className="py-16 bg-[#0D0D0D] relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-r from-[#EC2229]/5 to-[#1A1D23] border border-[#EC2229]/10 p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-4">
                  <Gift className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs text-emerald-400 font-semibold">Neukundenangebot</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                  1 File <span className="text-[#EC2229]">GRATIS</span><br />bei Neuanmeldung
                </h2>
                <p className="text-white/40 mb-6 leading-relaxed">
                  Registrieren Sie sich jetzt und erhalten Sie Ihren ersten Fileservice komplett kostenlos. Nutzen Sie unsere günstigen Credit-Pakete für weitere Aufträge.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span className="text-white/60">Keine Grundgebühr — nur zahlen, was Sie nutzen</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span className="text-white/60">Bonus-Credits bei größeren Paketen</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span className="text-white/60">Sofortige Gutschrift — direkt loslegen</span>
                  </div>
                </div>
                <Link to="/register">
                  <Button className="bg-[#EC2229] hover:bg-[#D01F25] text-white font-semibold px-8">
                    Jetzt kostenlos starten
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'Starter', desc: 'Ideal zum Testen' },
                  { name: 'Standard', desc: 'Für regelmäßige Nutzung' },
                  { name: 'Pro', desc: 'Beliebtes Paket' },
                  { name: 'Business', desc: 'Beste Ersparnis' },
                ].map(pkg => (
                  <div key={pkg.name} className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <p className="text-white font-semibold text-sm">{pkg.name}</p>
                    <p className="text-xs text-slate-400 mt-1">{pkg.desc}</p>
                    <p className="text-xs text-emerald-400 mt-2">Attraktive Rabatte</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="ablauf" className="py-24 bg-[#0D0D0D] relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-[#EC2229] tracking-[0.15em] uppercase mb-3">{t('landing.steps.title')}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
              {t('landing.steps.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-5 gap-4">
            {steps.map((step, i) => (
              <div key={i} className="relative group">
                <div className="p-6 rounded-xl bg-[#141418] border border-white/[0.04] group-hover:border-[#EC2229]/20 transition-all text-center h-full">
                  <div className="w-12 h-12 rounded-full bg-[#EC2229]/10 flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-5 h-5 text-[#EC2229]" />
                  </div>
                  <span className="text-3xl font-heading font-bold text-white/[0.06]">{step.num}</span>
                  <h3 className="font-heading text-base font-semibold text-white mt-1">{step.title}</h3>
                  <p className="text-xs text-white/30 mt-2 leading-relaxed">{step.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-4 h-4 text-[#EC2229]/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-[#0A0A0A] relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EC2229]/20 to-transparent" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-[#EC2229] tracking-[0.15em] uppercase mb-3">FAQ</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
              {t('landing.faq.title')}
            </h2>
          </div>
          
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="rounded-xl bg-[#141418] border border-white/[0.04] overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full p-5 text-left flex items-center justify-between group"
                  data-testid={`faq-${i}`}
                >
                  <span className="font-medium text-white text-sm pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#EC2229] flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-white/40 leading-relaxed border-t border-white/[0.04] pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chiptuning Starter Paket Section */}
      <section className="py-24 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#EC2229]/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EC2229]/10 rounded-full border border-[#EC2229]/20 mb-6">
                <Zap className="w-3.5 h-3.5 text-[#EC2229]" />
                <span className="text-xs text-[#EC2229] font-semibold uppercase tracking-wider">{t('landing.starter.badge')}</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                {t('landing.starter.title')}
              </h2>
              <p className="text-white/40 text-lg mb-8 leading-relaxed">
                {t('landing.starter.subtitle')}
              </p>
              <ul className="space-y-4 mb-8">
                {[t('landing.starter.feat1'), t('landing.starter.feat2'), t('landing.starter.feat3')].map((feat, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    </div>
                    <span className="text-white/70">{feat}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button className="bg-[#EC2229] hover:bg-[#D01F25] text-white font-semibold px-8 py-5 text-lg" data-testid="starter-cta">
                  {t('landing.starter.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/5 bg-[#141418] p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <Settings2 className="w-8 h-8 text-[#EC2229] mx-auto mb-3" />
                    <p className="text-sm text-white font-medium">Autotuner Tool</p>
                    <p className="text-xs text-white/30 mt-1">Slave Edition</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <Cpu className="w-8 h-8 text-[#EC2229] mx-auto mb-3" />
                    <p className="text-sm text-white font-medium">ECU Schulung</p>
                    <p className="text-xs text-white/30 mt-1">1:1 Training</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <CreditCard className="w-8 h-8 text-[#EC2229] mx-auto mb-3" />
                    <p className="text-sm text-white font-medium">5 Credits</p>
                    <p className="text-xs text-white/30 mt-1">Startguthaben</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <Users className="w-8 h-8 text-[#EC2229] mx-auto mb-3" />
                    <p className="text-sm text-white font-medium">Support</p>
                    <p className="text-xs text-white/30 mt-1">Unbegrenzt</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Dyno Image */}
      <section className="py-24 bg-[#0D0D0D] relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/hero/dyno-tuning.png" alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/80 to-[#0D0D0D]" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            {t('landing.cta.title1')}<br /><span className="text-[#EC2229]">{t('landing.cta.title2')}</span>
          </h2>
          <p className="text-lg text-white/40 mb-10">
            {t('landing.cta.subtitle')}
          </p>
          <Link to="/register">
            <Button className="bg-[#EC2229] hover:bg-[#D01F25] text-white text-lg px-10 py-6 font-semibold shadow-[0_0_40px_rgba(236,34,41,0.3)]" data-testid="cta-register">
              {t('landing.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="kontakt" className="bg-[#080808] border-t border-white/[0.04] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#EC2229] rounded-lg flex items-center justify-center">
                  <span className="font-heading font-bold text-white text-sm">TX</span>
                </div>
                <span className="font-heading font-bold text-white">TrXL Performance</span>
              </div>
              <p className="text-xs text-white/30 leading-relaxed">
                Professioneller ECU File Service für Tuning-Profis und Dealer weltweit.
              </p>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-white mb-4 text-sm">Services</h4>
              <ul className="space-y-2 text-xs text-white/30">
                <li><a href="#services" className="hover:text-white transition-colors">Stage 1 & 2 Tuning</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">DPF / EGR Delete</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">Pop & Bang</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">DSG / Launch Control</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-white mb-4 text-sm">Rechtliches</h4>
              <ul className="space-y-2 text-xs text-white/30">
                <li><Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link></li>
                <li><Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link></li>
                <li><Link to="/agb" className="hover:text-white transition-colors">AGB</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-white mb-4 text-sm">Kontakt</h4>
              <ul className="space-y-3 text-xs text-white/30">
                <li className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[#EC2229]" />
                  <a href="tel:+491725498156" className="hover:text-white transition-colors">+49 172 549 8156</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[#EC2229]" />
                  <a href="mailto:anfrage@trxl-performance.de" className="hover:text-white transition-colors">anfrage@trxl-performance.de</a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#EC2229] mt-0.5" />
                  <span>TrXL Performance<br />Schilfweg 5, 28857 Syke</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/[0.04] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/20">
              © {new Date().getFullYear()} TrXL Performance - Sven Trachsel. USt-ID: DE323629122
            </p>
            <div className="flex gap-6 text-xs text-white/20">
              <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
